import * as React from 'react';
import { connect } from 'react-redux';
import { Icon, PersonaSize } from 'office-ui-fabric-react';
import { ApplicationState } from '../../../store';
import { Resource, createResourceCalendar } from '../../../store/ResourcesListStore';
import { CalendarDataSet } from '../../../store/calendar/common';
import { getEntityCalendar } from '../../../store/CalendarsListStore';
import { FormatDate, formatFieldValue, formatValue } from '../../utils/common';
import { AssignmentsByDay, calculateAssignmentsSummary } from './ResourceUsageCell';
import { EntityType, mapServerEntityType } from '../../../entities/common';
import { FormatType } from '../../../entities/Metadata';
import { ImagePersonaCoin } from '../../common/ImagePersona';
import { TenantState } from '../../../store/Tenant';
import { UserState } from '../../../store/User';
import { getDefaultCostRateTable } from '../../../store/selectors/costRateTableSelectors';

type OwnProps = {
    resource: Resource;
    startDate: Date;
    finishDate: Date;
    entityType: EntityType;
    byDay: AssignmentsByDay | undefined;
};

type StateProps = {
    calendar: CalendarDataSet;
    resourcePlanningLevel: EntityType;
    tenant: TenantState;
    user: UserState;
    defaultCostRateTableId?: string;
}

type Props = OwnProps & StateProps;

const AssignmentsTooltipContent = (props: Props) => {
    const { startDate, finishDate, tenant, user, byDay, ...context } = props;
    const { resource, entityType, calendar } = context;
    const {
        availability,
        capacityHours,
        effort,
        completedWork,
        remainingWork,
        overage,
        hasLocalOverage,
        estimatedCharge,
        estimatedCost,
        actualCharge,
        actualCost
    } = calculateAssignmentsSummary(startDate, finishDate, context, byDay);

    const isSingleDay = startDate.getBeginOfDay().getTime() === finishDate.getBeginOfDay().getTime();
    const resourceCalendar = createResourceCalendar(resource, calendar);
    const calendarException = isSingleDay && resourceCalendar.exceptionsHoursMap[startDate.getBeginOfDay().getTime()] !== undefined
        ? resourceCalendar.exceptions.find(_ => _.start <= startDate && startDate <= _.end && _.days.includes(startDate.getDay()))
        : undefined;

    return <div className="timeline-tooltip">
        <div className="header">
            <ImagePersonaCoin className="logo" size={PersonaSize.size32} info={resource} text={resource.name} />
            <div className="title">
                <div className="overflow-text" title={resource.name}>
                    {resource.name}
                </div>
            </div>
        </div>
        <div className="content">
            {isSingleDay
                ? renderField("Date", <div className="no-overflow ellipsis">
                    {FormatDate(startDate, { weekday: 'long' })}
                    {calendarException ? <> - <span title={calendarException.name}>{calendarException.name}</span></> : ''}
                </div>)
                : renderField("Period", <div>{FormatDate(startDate)} - {FormatDate(finishDate)}</div>)}
            {renderField("Availability",
                <div><span className={`progress-tip ${availability > 0 ? '' : 'gray'}`}>{formatValue(availability)}</span> {getHoursUnit(availability)}</div>)}
            {renderField("Capacity", <div>{formatValue(capacityHours)} {getHoursUnit(capacityHours)}</div>)}
            {renderField("Effort", <div>{formatValue(effort)} {getHoursUnit(effort)}</div>)}
            {renderField("Completed Work", <div>{formatValue(completedWork)} {getHoursUnit(completedWork)}</div>)}
            {renderField("Remaining Work", <div>{formatValue(remainingWork)} {getHoursUnit(remainingWork)}</div>)}
            {renderField("Estimated Charge", <div>{formatFieldValue(estimatedCharge, FormatType.Cost, user, entityType, tenant)}</div>)}
            {renderField("Estimated Cost", <div>{formatFieldValue(estimatedCost, FormatType.Cost, user, entityType, tenant)}</div>)}
            {renderField("Actual Charge", <div>{formatFieldValue(actualCharge, FormatType.Cost, user, entityType, tenant)}</div>)}
            {renderField("Actual Cost", <div>{formatFieldValue(actualCost, FormatType.Cost, user, entityType, tenant)}</div>)}
            {renderField("Overallocated", overage > 0
                ? <div><span className="progress-tip red">{formatValue(overage)}</span> {getHoursUnit(overage)}</div>
                : hasLocalOverage
                    ? <div><Icon iconName="Warning" className="warning-icon" title="" />Some days</div>
                    : <div className="non-completed">n/a</div>)}
        </div>
    </div>;
}

const renderField = (name: string, valueElement: JSX.Element): JSX.Element => {
    return (
        <div className="item align-center">
            <div className="label">{name}</div>
            {valueElement}
        </div>
    );
}

const getHoursUnit = (hours: number) => hours === 1 ? "hour" : "hours";

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StateProps {
    return {
        calendar: getEntityCalendar(state.calendars, ownProps.resource, ownProps.resource.calendar.id),
        resourcePlanningLevel: mapServerEntityType[state.tenant.resourcePlanningSettings?.resourcePlanningLevel!]!,
        tenant: state.tenant,
        user: state.user,
        defaultCostRateTableId: getDefaultCostRateTable(state)?.id,
    };
}

export default connect(mapStateToProps)(AssignmentsTooltipContent);
